import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { UserAccess } from "../../types";
import { fetchUserAccess, setUserAccess } from "../../utils/api";
import { Banner, Icon } from "../common/ui";

/**
 * Grant or revoke a student's access, one sellable block at a time.
 *
 * The panel sends the DESIRED state rather than deltas: ticked blocks get the
 * chosen number of days added, un-ticked ones are revoked, in a single request.
 * That way a half-applied change — paid for, half granted — cannot happen, and
 * what the admin sees ticked is exactly what the student ends up owning.
 */
export default function AccessPanel({ userId, token }: { userId: string; token: string }) {
  const { t, i18n } = useTranslation();
  const isRu = !i18n.language?.startsWith("en");

  const [access, setAccess] = useState<UserAccess | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [days, setDays] = useState(30);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: "success" | "error"; text: string } | null>(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const a = await fetchUserAccess(userId, token);
      setAccess(a);
      setPicked(new Set(a.blocks.filter((b) => b.owned).map((b) => b.key)));
    } catch (e) {
      setMsg({ kind: "error", text: e instanceof Error ? e.message : "" });
    }
  }, [userId, token]);

  useEffect(() => { if (open && !access) load(); }, [open, access, load]);

  const toggle = (key: string) => setPicked((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const apply = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const a = await setUserAccess(userId, { blocks: [...picked], days }, token);
      setAccess(a);
      setPicked(new Set(a.blocks.filter((b) => b.owned).map((b) => b.key)));
      setMsg({ kind: "success", text: t("access.saved") });
    } catch (e) {
      setMsg({ kind: "error", text: e instanceof Error ? e.message : t("common.error") });
    } finally {
      setBusy(false);
    }
  };

  const owned = access?.blocks.filter((b) => b.owned) ?? [];

  return (
    <div className="glass glass-1" style={{ borderRadius: 12, overflow: "hidden" }}>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        style={{
          width: "100%", display: "flex", alignItems: "center", gap: 9, padding: "10px 12px",
          border: "none", background: "transparent", color: "inherit", cursor: "pointer", textAlign: "left",
        }}
      >
        <Icon name="wallet" size={16} />
        <span className="t-sm" style={{ fontWeight: 700, flex: 1 }}>{t("access.title")}</span>
        <span className="t-xs muted-2">
          {access
            ? (owned.length ? owned.map((b) => (isRu ? b.title_ru : b.title_en)).join(" · ") : t("access.none"))
            : ""}
        </span>
        <span style={{ color: "var(--text-3)", transform: open ? "rotate(180deg)" : "none", transition: "transform .25s" }}>
          <Icon name="chevronDown" size={16} />
        </span>
      </button>

      {open && (
        <div style={{ padding: "0 12px 12px" }}>
          {!access ? (
            <div style={{ display: "grid", placeItems: "center", padding: 16 }}>
              <div className="spinner spinner-amber" style={{ width: 22, height: 22, borderWidth: 3 }} />
            </div>
          ) : (
            <>
              {access.legacy_full_grant && (
                <div style={{ marginBottom: 10 }}>
                  <Banner kind="info">{t("access.legacyGrant")}</Banner>
                </div>
              )}

              <div className="stack" style={{ "--gap": "7px" } as React.CSSProperties}>
                {access.blocks.map((b) => {
                  const on = picked.has(b.key);
                  return (
                    <button
                      key={b.key}
                      onClick={() => toggle(b.key)}
                      role="switch"
                      aria-checked={on}
                      className="glass glass-1"
                      style={{
                        width: "100%", display: "flex", alignItems: "center", gap: 10,
                        padding: "9px 11px", borderRadius: 10, cursor: "pointer", textAlign: "left",
                        color: "inherit",
                        border: on ? "1px solid var(--violet-text)" : "1px solid var(--border)",
                      }}
                    >
                      <span style={{
                        width: 20, height: 20, borderRadius: 6, flex: "none", display: "grid", placeItems: "center",
                        background: on ? "var(--violet-fill)" : "var(--glass-3)",
                        color: on ? "var(--violet-ink)" : "transparent",
                      }}>
                        <Icon name="check" size={13} stroke={3} />
                      </span>
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span className="t-sm" style={{ fontWeight: 700, display: "block" }}>
                          {isRu ? b.title_ru : b.title_en}
                        </span>
                        <span className="t-xs muted-2">
                          {b.owned
                            ? t("access.daysLeft", { count: b.days_left })
                            : t("access.notOwned")}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>

              <label style={{ display: "block", marginTop: 11 }}>
                <span className="t-xs muted-2">{t("access.days")}</span>
                <input
                  type="number" min={0} max={3650} value={days}
                  onChange={(e) => setDays(Math.max(0, parseInt(e.target.value, 10) || 0))}
                  className="glass glass-1"
                  style={{
                    width: "100%", marginTop: 4, padding: "9px 11px", borderRadius: 10,
                    border: "1px solid var(--border)", background: "var(--glass-1)",
                    color: "var(--text-1)", font: "inherit", fontWeight: 700,
                  }}
                />
              </label>
              <p className="t-xs muted-2" style={{ margin: "6px 0 0", lineHeight: 1.4 }}>
                {t("access.hint")}
              </p>

              <button className="btn btn-violet btn-block" onClick={apply} disabled={busy} style={{ marginTop: 10 }}>
                {busy ? <span className="spinner" /> : <><Icon name="save" size={16} /> {t("access.apply")}</>}
              </button>

              {msg && <div style={{ marginTop: 9 }}><Banner kind={msg.kind}>{msg.text}</Banner></div>}
            </>
          )}
        </div>
      )}
    </div>
  );
}
